import { useId, useState, type FormEvent } from "react";
import {
  AlertCircle,
  ArrowRight,
  Eye,
  EyeOff,
} from "lucide-react";
import { Brand } from "../../components/Brand";
import { ApiError, authApi } from "../../services/api";
import { devTrace } from "../../services/devTrace";
import type { UserSession } from "../../types";

interface LoginPageProps {
  onAuthenticated: (session: UserSession) => void;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginPage({ onAuthenticated }: LoginPageProps) {
  const emailId = useId();
  const passwordId = useId();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");

    if (!emailPattern.test(email.trim())) {
      setError("Enter a valid business email address.");
      return;
    }
    if (password.length < 8) {
      setError("Password must contain at least 8 characters.");
      return;
    }

    setSubmitting(true);
    const started = performance.now();
    const normalized = email.trim().toLowerCase();
    try {
      const session = await authApi.login(normalized, password);
      devTrace.record({
        runId: session.id,
        step: "login",
        actor: "Authentication API",
        level: "success",
        summary: "Session established by the analytics service.",
        inputs: { email: normalized, password: "[REDACTED]" },
        durationMs: Math.round(performance.now() - started),
      });
      onAuthenticated(session);
    } catch (error) {
      const failure =
        error instanceof ApiError
          ? error
          : new ApiError(0, {
              code: "unexpected_error",
              message: "Sign-in failed.",
            });
      // The API's correlation id is what support can actually trace, so prefer
      // it over inventing one here.
      setError(
        failure.requestId
          ? `${failure.message} Reference: ${failure.requestId}`
          : failure.message,
      );
      devTrace.record({
        runId: failure.requestId ?? crypto.randomUUID(),
        step: "login",
        actor: "Authentication API",
        level: "error",
        summary: `Sign-in rejected: ${failure.code}.`,
        inputs: { email: normalized, password: "[REDACTED]" },
        durationMs: Math.round(performance.now() - started),
      });
    } finally {
      setSubmitting(false);
      setPassword("");
    }
  }

  return (
    <main className="login-page">
      <section className="login-brand-panel" aria-labelledby="login-brand-title">
        <Brand />
        <div>
          <p className="eyebrow">DMI Q Insight</p>
          <h1 id="login-brand-title">Turn governed data into decisions.</h1>
          <p className="login-lead">
            Build reports, dashboards, operational artifacts and advanced data
            analysis through guided conversation while keeping metrics, policies,
            and lineage consistent.
          </p>
        </div>
      </section>

      <section className="login-form-panel" aria-labelledby="sign-in-title">
        <div className="login-card">
          <p className="eyebrow dark">Secure access</p>
          <h2 id="sign-in-title">Sign in to your workspace</h2>
          <p className="muted">Use your DMI Q Insight account.</p>

          {error && (
            <div className="form-error" role="alert">
              <AlertCircle size={17} aria-hidden="true" />
              {error}
            </div>
          )}

          <form onSubmit={submit} noValidate>
            <label htmlFor={emailId}>Business email</label>
            <input
              id={emailId}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              placeholder="name@dminc.com"
              disabled={submitting}
              required
            />

            <div className="label-row">
              <label htmlFor={passwordId}>Password</label>
              <button
                type="button"
                className="text-button"
                onClick={() => setError("Password recovery will be connected with the authentication backend.")}
              >
                Forgot password?
              </button>
            </div>
            <div className="password-field">
              <input
                id={passwordId}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                placeholder="Enter your password"
                disabled={submitting}
                required
              />
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={() => setShowPassword((value) => !value)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            <button className="button primary login-submit" type="submit" disabled={submitting}>
              {submitting ? "Signing in…" : "Sign in"}
              {!submitting && <ArrowRight size={18} aria-hidden="true" />}
            </button>
          </form>
        </div>
      </section>
    </main>
  );
}
